import { useEffect, useState } from "react";
import api from "@/lib/api";

export const fiscalStates = {
  preparada: "Preparada · ainda não enviada", enviando: "Enviando", processando: "Em processamento",
  desconhecida: "Resultado incerto · consulte", rejeitada: "Rejeitada", autorizada: "Autorizada",
  contingencia: "Em contingência · consulte", cancelamento_pendente: "Cancelamento em processamento",
  cancelamento_incerto: "Cancelamento incerto · consulte", cancelada: "Cancelada",
};

export function useFiscalStatuses(orders, enabled) {
  const key = enabled ? orders.map(o => `${o.id}:${o.versao}`).sort().join(",") : "";
  const [result, setResult] = useState({ key: "", items: {} });
  useEffect(() => {
    if (!key) return;
    let active = true;
    let sequence = 0;
    async function load() {
      const request = ++sequence;
      const ids = key.split(",").map(part => Number(part.split(":")[0]));
      try {
        const { data } = await api.get(`/fiscal/orders?${ids.map(id => `order_ids=${id}`).join("&")}`);
        if (!Array.isArray(data.items)) throw new Error("Resposta fiscal indisponível");
        if (active && request === sequence) setResult({ key, items: Object.fromEntries(ids.map(id => [id,
          data.items.find(item => item.order_id === id) || { error: true }])) });
      } catch {
        if (active && request === sequence) setResult({ key, items: Object.fromEntries(ids.map(id => [id, { error: true }])) });
      }
    }
    load();
    const interval = window.setInterval(load, 30000);
    window.addEventListener("focus", load);
    window.addEventListener("fiscalAtualizado", load);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", load);
      window.removeEventListener("fiscalAtualizado", load);
    };
  }, [key]);
  return result.key === key ? result.items : {};
}

export function FiscalPending({ issues = [] }) {
  if (!issues.length) return null;
  return <section aria-label="Pendências para preparar a NFC-e" className="space-y-3 rounded-md border border-amber-800 bg-amber-950/30 p-3 text-sm text-amber-100">
    <h3 className="font-semibold">O que falta para preparar a nota</h3>
    <ul className="space-y-3">{issues.map(issue => <li key={issue.codigo}>
      <p className="font-medium">{issue.motivo}</p>
      <p className="mt-1 leading-relaxed">Como resolver: {issue.como_resolver}</p>
    </li>)}</ul>
    <p className="text-xs">Abrir esta tela não salva alterações na compra nem confirma o recebimento do pagamento. Use os botões de confirmação para salvar os dados conferidos.</p>
  </section>;
}

export function fiscalRecovery(message = "") {
  if (/dados estruturados conferidos/.test(message)) return "Confira os dados e use Validar dados da compra nesta janela. Se houver campos incorretos, corrija em Editar e salve. Recarregar apenas consulta os dados salvos.";
  if (/POS|maquininha/.test(message)) return "Use Confirmar POS e validar dados nesta janela somente se os cartões foram processados em maquininha independente. Você também pode conferir cada pagamento em Editar → Pagamentos.";
  if (/endereço|CPF/.test(message)) return "Confira o CPF e os campos separados do endereço em Editar e salve. O texto livre do endereço não substitui os campos estruturados.";
  if (/perfil|cadastro fiscal|Produto sem cadastro/i.test(message)) return "Confira o cadastro fiscal dos produtos com a contabilidade antes de tentar preparar a nota novamente.";
  if (/alterada|versão|recarregue/i.test(message)) return "Recarregue os dados e confira a versão atual da compra antes de confirmar outra ação.";
  return "Recarregue os dados e consulte a situação da nota antes de repetir a operação. Se persistir, informe a mensagem exibida ao administrador.";
}

export function FiscalDocumentMessage({ document }) {
  const result = document?.resultado || {};
  const uncertain = ["desconhecida", "enviando", "processando", "contingencia", "cancelamento_incerto", "cancelamento_pendente"].includes(document?.situacao);
  return <>
    {result.mensagem_sefaz && <p className="text-sm break-words">Retorno SEFAZ{result.status_sefaz ? ` (${result.status_sefaz})` : ""}: {result.mensagem_sefaz}</p>}
    {result.erro_tecnico && <p className="text-sm break-words">Falha técnica registrada: {result.erro_tecnico}. Consulte a situação antes de tentar outra operação.</p>}
    {document?.situacao === "rejeitada" && <p className="text-sm">A nota não foi autorizada. Confira o retorno acima e corrija a pendência indicada. A correção usa a mesma referência fiscal.</p>}
    {uncertain && <p className="text-sm">A autorização ainda não está confirmada. Use Consultar situação e arquivos; não prepare outra nota para esta compra.</p>}
  </>;
}

export default function FiscalStatus({ data }) {
  if (!data) return <p className="text-xs text-muted-foreground">Consultando NFC-e…</p>;
  if (data.error) return <p className="text-xs text-amber-200">Situação fiscal indisponível. Abra NFC-e para consultar; não é possível afirmar se já foi emitida.</p>;
  const doc = data.documento;
  const issues = data.pendencias || [];
  const authorized = doc?.situacao === "autorizada";
  const label = authorized ? "NFC-e emitida · autorizada" : doc ? fiscalStates[doc.situacao] || "Situação fiscal não reconhecida · consulte" : "Sem NFC-e";
  return <div className="space-y-1 text-xs break-words whitespace-normal" aria-label="Situação da NFC-e">
    <p className={`font-semibold ${authorized ? "text-emerald-200" : issues.length || doc?.situacao === "rejeitada" ? "text-amber-200" : "text-muted-foreground"}`}>{label}</p>
    {doc && <p className="text-muted-foreground">{doc.ambiente === "homologacao" ? "Homologação · sem valor fiscal" : "Produção"}{doc.resultado?.numero ? ` · nº ${doc.resultado.numero}` : ""}{doc.resultado?.serie ? ` · série ${doc.resultado.serie}` : ""}</p>}
    <FiscalDocumentMessage document={doc} />
    {issues.length > 0 ? <>
      <p>{issues[0].motivo}</p>
      <details><summary className="cursor-pointer py-1 underline underline-offset-2 focus-visible:outline focus-visible:outline-2">Como resolver{issues.length > 1 ? ` · ${issues.length} pendências` : ""}</summary>
        <ul className="space-y-2 py-1">{issues.map(issue => <li key={issue.codigo}>{issues.length > 1 && <p className="font-medium">{issue.motivo}</p>}<p>{issue.como_resolver}</p></li>)}</ul>
      </details>
    </> : !doc && <p>Sem bloqueios locais identificados. A emissão é manual pelo botão NFC-e.</p>}
  </div>;
}
